import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  Paper,
  TextField,
  InputAdornment,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Skeleton,
  useTheme,
  useMediaQuery,
  alpha,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  PeopleAlt as PeopleIcon,
  WifiTethering as OnlineIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  PortableWifiOff as PortableWifiOffIcon,
  Block as BlockIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { Nas, ActiveClient } from '../../types';
import { nasService } from '../../services/nasService';
import SysKpiCard from '../common/SysKpiCard';

interface NasActiveClientsModalProps {
  open: boolean;
  nas: Nas | null;
  onClose: () => void;
}

type SortField = 'usuario' | 'ip' | 'uptime' | 'cliente' | 'status_servico';
type SortOrder = 'asc' | 'desc';

export const NasActiveClientsModal: React.FC<NasActiveClientsModalProps> = ({
  open,
  nas,
  onClose,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState<ActiveClient[]>([]);
  const [metrics, setMetrics] = useState({
    total_ativos: 0,
    total_online: 0,
    total_offline: 0,
    total_bloqueados: 0,
  });
  const [searchTerm, setSearchTerm] = useState('');

  // Ordenação: Padrão por usuário crescente (asc)
  const [orderBy, setOrderBy] = useState<SortField>('usuario');
  const [order, setOrder] = useState<SortOrder>('asc');

  const fetchActiveClients = useCallback(async () => {
    if (!nas) return;
    try {
      setLoading(true);
      const res = await nasService.getActiveClients(nas.id);
      setClients(res.clients || []);
      setMetrics({
        total_ativos: res.total_ativos || 0,
        total_online: res.total_online || 0,
        total_offline: res.total_offline || 0,
        total_bloqueados: res.total_bloqueados || 0,
      });
    } catch (error: any) {
      console.error('Erro ao buscar clientes online:', error);
      enqueueSnackbar(
        error?.response?.data?.message || 'Erro ao carregar clientes online do NAS',
        { variant: 'error' }
      );
      setClients([]);
      setMetrics({ total_ativos: 0, total_online: 0, total_offline: 0, total_bloqueados: 0 });
    } finally {
      setLoading(false);
    }
  }, [nas, enqueueSnackbar]);

  useEffect(() => {
    if (open && nas) {
      setSearchTerm('');
      setOrderBy('usuario');
      setOrder('asc');
      fetchActiveClients();
    }
  }, [open, nas, fetchActiveClients]);

  const handleRequestSort = (property: SortField) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const filteredClients = clients.filter((item) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return (
      item.usuario.toLowerCase().includes(term) ||
      item.ip.toLowerCase().includes(term) ||
      item.mac.toLowerCase().includes(term) ||
      item.cliente.toLowerCase().includes(term) ||
      item.contrato.toLowerCase().includes(term) ||
      (item.status_servico && item.status_servico.toLowerCase().includes(term)) ||
      item.uptime.toLowerCase().includes(term)
    );
  });

  const ipToNum = (ip: string) => {
    const parts = (ip || '').split('.').map(Number);
    if (parts.length === 4 && parts.every((p) => !isNaN(p))) {
      return parts[0] * 16777216 + parts[1] * 65536 + parts[2] * 256 + parts[3];
    }
    return 0;
  };

  const parseUptimeToSeconds = (uptime: string): number => {
    if (!uptime || uptime === '-') return 0;
    let seconds = 0;
    const str = uptime.toLowerCase();

    const wMatch = str.match(/(\d+)w/);
    if (wMatch) seconds += parseInt(wMatch[1]) * 604800;

    const dMatch = str.match(/(\d+)d/);
    if (dMatch) seconds += parseInt(dMatch[1]) * 86400;

    const hMatch = str.match(/(\d+)h/);
    if (hMatch) seconds += parseInt(hMatch[1]) * 3600;

    const mMatch = str.match(/(\d+)m/);
    if (mMatch) seconds += parseInt(mMatch[1]) * 60;

    const sMatch = str.match(/(\d+)s/);
    if (sMatch) seconds += parseInt(sMatch[1]);

    if (str.includes(':')) {
      const parts = str.split(':').map(Number);
      if (parts.length === 3 && parts.every((p) => !isNaN(p))) {
        seconds += parts[0] * 3600 + parts[1] * 60 + parts[2];
      } else if (parts.length === 2 && parts.every((p) => !isNaN(p))) {
        seconds += parts[0] * 60 + parts[1];
      }
    }

    return seconds;
  };

  const sortedClients = [...filteredClients].sort((a, b) => {
    let cmp = 0;
    if (orderBy === 'ip') {
      cmp = ipToNum(a.ip) - ipToNum(b.ip);
    } else if (orderBy === 'uptime') {
      cmp = parseUptimeToSeconds(a.uptime) - parseUptimeToSeconds(b.uptime);
    } else {
      const valA = a[orderBy] || '';
      const valB = b[orderBy] || '';
      cmp = String(valA).localeCompare(String(valB), 'pt-BR', { sensitivity: 'base', numeric: true });
    }

    return order === 'asc' ? cmp : -cmp;
  });

  const renderStatusServico = (status: string) => {
    const norm = (status || '').toLowerCase();
    switch (norm) {
      case 'ativo':
        return <Chip label="Ativo" size="small" color="success" sx={{ height: 22, fontSize: '0.75rem', fontWeight: 600 }} />;
      case 'bloqueado':
        return <Chip label="Bloqueado" size="small" color="error" sx={{ height: 22, fontSize: '0.75rem', fontWeight: 600 }} />;
      case 'suspenso':
        return <Chip label="Suspenso" size="small" color="warning" sx={{ height: 22, fontSize: '0.75rem', fontWeight: 600 }} />;
      case 'cancelado':
        return <Chip label="Cancelado" size="small" sx={{ height: 22, fontSize: '0.75rem', color: 'text.secondary', bgcolor: 'action.disabledBackground' }} />;
      case 'aguardando_instalacao':
        return <Chip label="Aguard. Instalação" size="small" color="info" variant="outlined" sx={{ height: 22, fontSize: '0.75rem' }} />;
      case 'aguardando_assinatura':
        return <Chip label="Aguard. Assinatura" size="small" color="info" variant="outlined" sx={{ height: 22, fontSize: '0.75rem' }} />;
      default:
        return <Chip label={status || 'Não cadastrado'} size="small" variant="outlined" sx={{ height: 22, fontSize: '0.75rem', color: 'text.disabled' }} />;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          minHeight: isMobile ? '100vh' : '65vh',
          maxHeight: isMobile ? '100vh' : '92vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: { xs: 1.5, sm: 2 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${theme.palette.divider}`,
          gap: 1,
        }}
      >
        <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0, flex: 1 }}>
          <OnlineIcon color="success" sx={{ fontSize: { xs: 24, sm: 28 }, flexShrink: 0 }} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="h6"
              fontWeight={700}
              component="div"
              sx={{
                lineHeight: 1.2,
                fontSize: { xs: '0.95rem', sm: '1.25rem' },
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              Clientes Online (PPPoE)
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: 'block',
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              NAS: <strong>{nas?.shortname || nas?.nasname}</strong> ({nas?.nasname})
            </Typography>
          </Box>
        </Box>

        <Box display="flex" alignItems="center" gap={{ xs: 0.5, sm: 1 }} sx={{ flexShrink: 0 }}>
          {!isMobile && (
            <Chip
              icon={<PeopleIcon sx={{ fontSize: '1rem !important' }} />}
              label={`${metrics.total_online} Online`}
              color="success"
              size="small"
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
          )}
          <Tooltip title="Atualizar conexões">
            <span>
              <IconButton onClick={fetchActiveClients} disabled={loading} size="small" color="primary">
                <RefreshIcon fontSize={isMobile ? 'small' : 'medium'} />
              </IconButton>
            </span>
          </Tooltip>
          <IconButton onClick={onClose} size="small" aria-label="close">
            <CloseIcon fontSize={isMobile ? 'small' : 'medium'} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 1.25, sm: 2 }, display: 'flex', flexDirection: 'column', gap: { xs: 1.25, sm: 2 }, flex: 1, minHeight: 0 }}>
        {/* Cards KPI no Início */}
        <Grid container spacing={{ xs: 1, sm: 1.5 }} sx={{ mt: 0.1 }}>
          <Grid item xs={6} sm={3}>
            <SysKpiCard
              titulo="Ativos"
              valor={metrics.total_ativos}
              subtitulo={isMobile ? undefined : "Serviços não cancelados/suspensos"}
              icone={<AssignmentTurnedInIcon sx={{ color: theme.palette.primary.main }} />}
              cor={theme.palette.primary.main}
              bgCor={alpha(theme.palette.primary.main, 0.12)}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <SysKpiCard
              titulo="Online"
              valor={metrics.total_online}
              subtitulo={isMobile ? undefined : "Conectados no active"}
              icone={<OnlineIcon sx={{ color: theme.palette.success.main }} />}
              cor={theme.palette.success.main}
              bgCor={alpha(theme.palette.success.main, 0.12)}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <SysKpiCard
              titulo="Offline"
              valor={metrics.total_offline}
              subtitulo={isMobile ? undefined : "Ativos - Online"}
              icone={<PortableWifiOffIcon sx={{ color: theme.palette.warning.main }} />}
              cor={theme.palette.warning.main}
              bgCor={alpha(theme.palette.warning.main, 0.12)}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <SysKpiCard
              titulo="Bloqueado"
              valor={metrics.total_bloqueados}
              subtitulo={isMobile ? undefined : "Online com serviço bloqueado"}
              icone={<BlockIcon sx={{ color: theme.palette.error.main }} />}
              cor={theme.palette.error.main}
              bgCor={alpha(theme.palette.error.main, 0.12)}
            />
          </Grid>
        </Grid>

        {/* Barra de Pesquisa */}
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
          <TextField
            size="small"
            placeholder="Pesquisar por Usuário, IP, MAC, Cliente, Contrato..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ width: '100%', flexGrow: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          {searchTerm && (
            <Typography variant="caption" color="text.secondary">
              Exibindo {sortedClients.length} de {clients.length} conexões
            </Typography>
          )}
        </Box>

        {/* Conteúdo da Tabela */}
        <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden', flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <TableContainer sx={{ overflowX: 'auto', flexGrow: 1, maxHeight: isMobile ? 'none' : '55vh' }}>
            <Table size="small" stickyHeader sx={{ minWidth: 700 }}>
              <TableHead>
                <TableRow
                  sx={{
                    '& .MuiTableCell-head': {
                      bgcolor: (t) => `color-mix(in srgb, ${t.palette.primary.main} 12%, ${t.palette.background.paper})`,
                      fontWeight: 700,
                      fontSize: '0.73rem',
                      py: 0.8,
                      borderBottom: (t) => `2px solid ${alpha(t.palette.primary.main, 0.2)}`,
                      whiteSpace: 'nowrap',
                    },
                  }}
                >
                  {/* Usuário */}
                  <TableCell sortDirection={orderBy === 'usuario' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'usuario'}
                      direction={orderBy === 'usuario' ? order : 'asc'}
                      onClick={() => handleRequestSort('usuario')}
                    >
                      Usuário
                    </TableSortLabel>
                  </TableCell>

                  {/* IP */}
                  <TableCell sortDirection={orderBy === 'ip' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'ip'}
                      direction={orderBy === 'ip' ? order : 'asc'}
                      onClick={() => handleRequestSort('ip')}
                    >
                      IP
                    </TableSortLabel>
                  </TableCell>

                  {/* MAC */}
                  <TableCell>MAC</TableCell>

                  {/* Tempo de Conexão */}
                  <TableCell sortDirection={orderBy === 'uptime' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'uptime'}
                      direction={orderBy === 'uptime' ? order : 'asc'}
                      onClick={() => handleRequestSort('uptime')}
                    >
                      Tempo de conexão (uptime)
                    </TableSortLabel>
                  </TableCell>

                  {/* Cliente */}
                  <TableCell sortDirection={orderBy === 'cliente' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'cliente'}
                      direction={orderBy === 'cliente' ? order : 'asc'}
                      onClick={() => handleRequestSort('cliente')}
                    >
                      Cliente
                    </TableSortLabel>
                  </TableCell>

                  {/* Contrato */}
                  <TableCell>Contrato</TableCell>

                  {/* Status Serviço */}
                  <TableCell align="center" sortDirection={orderBy === 'status_servico' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'status_servico'}
                      direction={orderBy === 'status_servico' ? order : 'asc'}
                      onClick={() => handleRequestSort('status_servico')}
                    >
                      Status Serviço
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 7 }).map((_, colIdx) => (
                        <TableCell key={colIdx} sx={{ py: 0.5 }}>
                          <Skeleton />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : sortedClients.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.disabled">
                        {searchTerm ? 'Nenhum resultado encontrado para a pesquisa' : 'Nenhum cliente online neste NAS'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedClients.map((client, idx) => (
                    <TableRow
                      key={`${client.usuario}-${client.ip}-${idx}`}
                      hover
                      sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.03) } }}
                    >
                      {/* Usuário */}
                      <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                        <Typography variant="body2" fontWeight={600} color="primary.main" sx={{ fontSize: '0.78rem' }}>
                          {client.usuario}
                        </Typography>
                      </TableCell>

                      {/* IP */}
                      <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                        {client.ip && client.ip !== '-' ? (
                          <Tooltip title={`Abrir http://${client.ip} em nova aba`}>
                            <Typography
                              variant="body2"
                              component="a"
                              href={`http://${client.ip}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              sx={{
                                fontFamily: 'monospace',
                                color: 'primary.main',
                                fontWeight: 600,
                                fontSize: '0.78rem',
                                textDecoration: 'none',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                '&:hover': { textDecoration: 'underline' },
                              }}
                            >
                              {client.ip}
                              <OpenInNewIcon sx={{ fontSize: '0.8rem' }} />
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'text.secondary', fontSize: '0.78rem' }}>
                            {client.ip || '-'}
                          </Typography>
                        )}
                      </TableCell>

                      {/* MAC */}
                      <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'text.secondary', fontSize: '0.78rem' }}>
                          {client.mac}
                        </Typography>
                      </TableCell>

                      {/* Tempo de Conexão */}
                      <TableCell sx={{ py: 0.5, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                        <Chip
                          label={client.uptime}
                          size="small"
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.7rem', fontWeight: 500 }}
                        />
                      </TableCell>

                      {/* Cliente */}
                      <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                        {client.cliente_id && client.cliente !== 'Cliente não encontrado' ? (
                          <Tooltip title="Abrir página do cliente em nova aba">
                            <Typography
                              variant="body2"
                              component="a"
                              href={`/clients/${client.cliente_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              sx={{
                                fontWeight: 600,
                                color: 'primary.main',
                                fontSize: '0.78rem',
                                textDecoration: 'none',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                '&:hover': { textDecoration: 'underline' },
                              }}
                            >
                              {client.cliente}
                              <OpenInNewIcon sx={{ fontSize: '0.8rem' }} />
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" fontWeight={400} color="text.disabled" sx={{ fontSize: '0.78rem' }}>
                            {client.cliente}
                          </Typography>
                        )}
                      </TableCell>

                      {/* Contrato */}
                      <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                        {client.contrato_id && client.contrato !== 'Contrato não encontrado' ? (
                          <Tooltip title="Abrir página do contrato em nova aba">
                            <Typography
                              variant="body2"
                              component="a"
                              href={`/contracts/${client.contrato_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              sx={{
                                fontWeight: 500,
                                color: 'primary.main',
                                fontSize: '0.78rem',
                                textDecoration: 'none',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                '&:hover': { textDecoration: 'underline' },
                              }}
                            >
                              {client.contrato}
                              <OpenInNewIcon sx={{ fontSize: '0.8rem' }} />
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.disabled" sx={{ fontSize: '0.78rem' }}>
                            {client.contrato}
                          </Typography>
                        )}
                      </TableCell>

                      {/* Status Serviço */}
                      <TableCell align="center" sx={{ py: 0.5 }}>
                        {renderStatusServico(client.status_servico)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2, sm: 2.5 }, py: { xs: 1, sm: 1.5 }, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Button onClick={onClose} variant="outlined" color="inherit" size={isMobile ? 'small' : 'medium'} fullWidth={isMobile}>
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NasActiveClientsModal;

